/*
 * tailwind.config.js
 * ---------------------------------------
 *
 * This config file overwrites the DEFAULT tailwindcss config to meet the needs of the Yesplan App design system.
 *
 */

module.exports = {
  content: ['./app/**/*.{js,jsx}', './stories/**/*.{js,jsx}'],
  important: false,
  separator: ':',
  theme: {
    screens: {
      md: '600px',
      lg: '960px'
    },
    colors: {
      transparent: 'transparent',
      current: 'currentColor',
      black: '#000000',
      white: '#FFFFFF',
      gray: {
        base: '#4c4c4c',
        'lighten-30': '#8f8f8f',
        'lighten-20': '#787878',
        'lighten-10': '#616161',
        'darken-10': '#383838',
        'darken-20': '#242424',
        'darken-30': '#121212'
      },
      silver: {
        base: '#cccccc',
        'lighten-30': '#fafafa',
        'lighten-25': '#f2f2f2',
        'lighten-20': '#eaeaea',
        'lighten-15': '#e3e3e3',
        'lighten-10': '#dbdbdb',
        'lighten-5': '#d4d4d4',
        'darken-5': '#c5c5c5',
        'darken-10': '#bdbdbd',
        'darken-15': '#b6b6b6',
        'darken-20': '#aeaeae',
        'darken-25': '#a7a7a7',
        'darken-30': '#a0a0a0'
      },

      blue: {
        base: '#2133f2',
        'lighten-40': '#ecedfe',
        'lighten-30': '#CFD3FC',
        'lighten-20': '#959DF9',
        'lighten-10': '#5B68F5',
        'darken-10': '#0C1CCA',
        'darken-20': '#081491',
        'darken-30': '#050C57'
      },
      cyan: {
        base: '#21BAF2',
        'lighten-40': '#ecf9fe',
        'lighten-30': '#CFF0FC',
        'lighten-20': '#95DEF9',
        'lighten-10': '#5BCCF5',
        'darken-10': '#0C98CA',
        'darken-20': '#086C91',
        'darken-30': '#054157'
      },
      red: {
        base: '#F22821',
        'lighten-40': '#feecec',
        'lighten-30': '#FCD0CF',
        'lighten-20': '#F99895',
        'lighten-10': '#F5605B',
        'darken-10': '#CA120C',
        'darken-20': '#910D08',
        'darken-30': '#570805'
      },
      orange: {
        base: '#F29421',
        'lighten-40': '#fef6ec',
        'lighten-30': '#FCE8CF',
        'lighten-20': '#F9CC95',
        'lighten-10': '#F5B05B',
        'darken-10': '#CA750C',
        'darken-20': '#915308',
        'darken-30': '#573205'
      },
      green: {
        base: '#21F248',
        'lighten-40': '#ecfeef',
        'lighten-30': '#CFFCD7',
        'lighten-20': '#95F9A7',
        'lighten-10': '#5BF577',
        'darken-10': '#0CCA2F',
        'darken-20': '#089121',
        'darken-30': '#055714'
      },
      yellow: {
        base: '#FFDA1A'
      }
    },
    spacing: {
      px: '1px',
      0: '0',
      2: '.125rem',
      4: '.25rem',
      6: '.375rem',
      8: '.5rem',
      12: '.75rem',
      16: '1rem',
      20: '1.25rem',
      24: '1.5rem',
      28: '1.75rem',
      32: '2rem',
      40: '2.5rem',
      48: '3rem'
    },
    backgroundColor: (theme) => ({
      ...theme('colors'),
      inherit: 'inherit'
    }),
    borderColor: (theme) => ({
      ...theme('colors'),
      DEFAULT: theme('colors.gray.80', 'currentColor')
    }),
    borderRadius: {
      none: '0',
      DEFAULT: '0.25rem',
      full: '9999px'
    },
    borderWidth: {
      DEFAULT: '1px',
      0: '0',
      2: '2px',
      4: '4px'
    },
    boxShadow: {
      white: '0px 1px 3px 0px rgba(255,255,255,0.2)'
    },
    container: {},
    cursor: {
      auto: 'auto',
      DEFAULT: 'DEFAULT',
      pointer: 'pointer',
      wait: 'wait',
      text: 'text',
      move: 'move',
      'not-allowed': 'not-allowed'
    },
    fill: (theme) => ({
      ...theme('colors'),
      current: 'currentColor'
    }),
    flex: {
      1: '1 1 0%',
      auto: '1 1 auto',
      initial: '0 1 auto',
      none: 'none'
    },
    flexGrow: {
      0: '0',
      DEFAULT: '1'
    },
    flexShrink: {
      0: '0',
      DEFAULT: '1'
    },
    fontFamily: {
      sans: [
        'ui-sans-serif',
        'system-ui',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
        '"Noto Color Emoji"'
      ],
      mono: [
        'ui-monospace',
        'SFMono-Regular',
        'Menlo',
        'Monaco',
        'Consolas',
        'Liberation Mono',
        'Courier New',
        'monospace'
      ]
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.8125rem',
      md: '0.9375rem',
      lg: '1.0625rem',
      xl: '1.1875rem'
    },
    fontWeight: {
      normal: '400',
      medium: '500',
      bold: '700'
    },
    height: (theme) => ({
      auto: 'auto',
      ...theme('spacing'),
      full: '100%',
      screen: '100vh'
    }),
    inset: {
      0: '0',
      auto: 'auto',
      '1/2': '50%'
    },
    letterSpacing: {
      normal: '0',
      wide: '0.025em'
    },
    lineHeight: {
      none: '1rem',
      normal: '1.5rem',
      16: '1rem',
      18: '1.125rem',
      20: '1.25rem',
      22: '1.375rem',
      28: '1.75rem',
      32: '2rem'
    },
    margin: (theme, { negative }) => ({
      auto: 'auto',
      ...theme('spacing'),
      ...negative(theme('spacing'))
    }),
    maxHeight: {
      full: '100%',
      screen: '100vh'
    },
    maxWidth: (theme) => ({
      none: 'none',
      xs: '20rem',
      sm: '24rem',
      md: '28rem',
      lg: '32rem',
      xl: '36rem',
      '2xl': '42rem',
      '3xl': '48rem',
      '4xl': '54rem',
      '5xl': '60rem',
      '1/3': '33.3%',
      '1/4': '25%',
      '2/4': '50%',
      '1/5': '20%',
      '3/5': '60%',
      full: '100%',
      'screen-md': theme('screens.md'),
      'screen-lg': theme('screens.lg')
    }),
    minHeight: {
      0: '0',
      full: '100%',
      screen: '100vh'
    },
    minWidth: {
      0: '0',
      full: '100%'
    },
    opacity: {
      0: '0',
      10: '.1',
      20: '.2',
      45: '.45',
      50: '.5',
      60: '.6',
      65: '.65',
      85: '.85',
      95: '.95',
      100: '1'
    },
    backgroundOpacity: {
      10: '0.1',
      20: '0.2',
      50: '.5',
      80: '.8',
      90: '0.90'
    },
    padding: (theme) => theme('spacing'),
    placeholderColor: (theme) => theme('colors'),
    stroke: {
      current: 'currentColor'
    },
    textColor: (theme) => ({
      ...theme('colors')
    }),
    transitionDuration: {
      100: '100ms'
    },
    width: (theme) => ({
      auto: 'auto',
      ...theme('spacing'),
      '1/2': '50%',
      '1/3': '33.333333%',
      '2/3': '66.666667%',
      '1/4': '25%',
      '2/4': '50%',
      '3/4': '75%',
      '1/5': '20%',
      '2/5': '40%',
      '3/5': '60%',
      '4/5': '80%',
      full: '100%',
      screen: '100vw'
    }),
    zIndex: {
      auto: 'auto',
      0: '0',
      1: '1',
      2: '2',
      3: '3',
      100: '100',
      1000: '1000'
    }
  },
  plugins: [],
  purge: false,
  extend: {
    headerHeight: 44
  }
};
